import { router } from '@inertiajs/react';
import {
    ReactNode,
    createContext,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';
import { I18nProvider } from 'react-aria-components';
import { IntlProvider } from 'react-intl';

import fetchTranslations from '../../../shared/fetchTranslations';
import LocaleCode from '../../types/LocaleCode';

type LocalizationProps = {
    switchLocale: (locale: LocaleCode) => void;
    currentLocale?: LocaleCode;
    locales: Record<string, LocaleCode>;
};

const LocalizationContext = createContext<LocalizationProps>({
    switchLocale: () => {},
    currentLocale: undefined,
    locales: {},
});

export const useLocalization = () => {
    return useContext(LocalizationContext);
};

type LocalizationProviderProps = {
    translationsUrl: string;
    currentLocale: LocaleCode;
    locales: Record<string, LocaleCode>;
    translations: Record<string, string>;
    children: ReactNode;
};

export const LocalizationProvider = ({
    translationsUrl,
    currentLocale,
    translations,
    locales,
    children,
}: LocalizationProviderProps) => {
    const [locale, setLocale] = useState(currentLocale);
    const translationsRef = useRef(translations);

    const switchLocale = (newLocale: LocaleCode) => {
        const newUrl = window.location.href.replace(
            new RegExp(`/${locale.urlSegment}/`),
            `/${newLocale.urlSegment}/`
        );

        /**
         * We're working around Inertia's limitations here because the `setup`
         * function is not called when we navigate to a new page. This means
         * that the translations are not updated when we switch the locale.
         * To work around this, we're manually updating the translations
         * and the locale after the navigation is complete.
         */
        router.visit(newUrl, {
            replace: true,
            onSuccess: () => {
                fetchTranslations({
                    url: translationsUrl.replace('%s', newLocale.urlSegment),
                }).then((translations) => {
                    translationsRef.current = translations;
                    setLocale(newLocale);
                });
            },
        });
    };

    const providerValue = useMemo<LocalizationProps>(() => {
        return {
            switchLocale,
            translations: translationsRef.current,
            currentLocale: locale,
            locales,
        };
    }, [locale]);

    return (
        <IntlProvider
            messages={translationsRef.current}
            locale={locale.ietfLanguageTag}
        >
            <I18nProvider locale={locale.ietfLanguageTag}>
                <LocalizationContext.Provider value={providerValue}>
                    {children}
                </LocalizationContext.Provider>
            </I18nProvider>
        </IntlProvider>
    );
};
